import { endOfDay, startOfDay, subDays } from 'date-fns';
import {
  NominationFilters,
  NominationStats,
  NominatorOption,
  SuccessNominationRecord,
} from '../nomination.types';

export const EMPTY_NOMINATION_FILTERS: NominationFilters = {
  search: '',
  nominatorUid: '',
  location: '',
  dateFrom: null,
  dateTo: null,
};

/** Converts "Lastname, Firstname" into "Firstname Lastname" for display and search. */
export function normalizeClientName(name: string | null | undefined): string {
  if (!name) return '';
  if (!name.includes(',')) return name.trim();
  const [lastName, firstName] = name.split(',').map(part => part.trim());
  return `${firstName} ${lastName}`.trim();
}

/** Inclusive date-range test; `to` is extended to the end of its day. */
export function isWithinDateRange(date: Date, from: Date | null, to: Date | null): boolean {
  if (from && date < startOfDay(from)) return false;
  if (to && date > endOfDay(to)) return false;
  return true;
}

export function filterNominations(
  records: SuccessNominationRecord[],
  filters: NominationFilters
): SuccessNominationRecord[] {
  const search = filters.search.trim().toLowerCase();

  return records.filter(record => {
    if (search) {
      const haystack = [
        normalizeClientName(record.client_name),
        record.client_name,
        record.client_id,
        record.nominated_by_name,
      ]
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }

    if (filters.nominatorUid && record.nominated_by_uid !== filters.nominatorUid) return false;
    if (filters.location && record.client_location !== filters.location) return false;
    if (!isWithinDateRange(record.created_at, filters.dateFrom, filters.dateTo)) return false;

    return true;
  });
}

export function countActiveFilters(filters: NominationFilters): number {
  let count = 0;
  if (filters.search.trim()) count += 1;
  if (filters.nominatorUid) count += 1;
  if (filters.location) count += 1;
  if (filters.dateFrom || filters.dateTo) count += 1;
  return count;
}

/** Unique nominators present in the scoped data, sorted by display name. */
export function buildNominatorOptions(records: SuccessNominationRecord[]): NominatorOption[] {
  const byUid = new Map<string, string>();
  records.forEach(record => {
    if (!record.nominated_by_uid) return;
    if (!byUid.has(record.nominated_by_uid)) {
      byUid.set(record.nominated_by_uid, record.nominated_by_name || 'Unknown staff');
    }
  });
  return Array.from(byUid.entries())
    .map(([uid, name]) => ({ uid, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Unique client locations present in the scoped data, sorted alphabetically. */
export function buildLocationOptions(records: SuccessNominationRecord[]): string[] {
  const locations = new Set<string>();
  records.forEach(record => {
    if (record.client_location) locations.add(record.client_location);
  });
  return Array.from(locations).sort((a, b) => a.localeCompare(b));
}

export function buildNominationStats(records: SuccessNominationRecord[], now: Date = new Date()): NominationStats {
  const thirtyDaysAgo = subDays(now, 30);
  const clients = new Set<string>();
  const nominators = new Set<string>();
  let last30Days = 0;

  records.forEach(record => {
    clients.add(record.client_id);
    nominators.add(record.nominated_by_uid);
    if (record.created_at >= thirtyDaysAgo) last30Days += 1;
  });

  return {
    total: records.length,
    last30Days,
    uniqueClients: clients.size,
    uniqueNominators: nominators.size,
  };
}

/** Short, single-line preview of free text for table cells and notifications. */
export function excerpt(text: string, maxLength = 140): string {
  const collapsed = text.replace(/\s+/g, ' ').trim();
  if (collapsed.length <= maxLength) return collapsed;
  return `${collapsed.slice(0, maxLength - 1).trimEnd()}…`;
}
